import dayjs from 'dayjs';
import { BlindTypeMap, RiskTypeMap } from '../../../types/process/order';
import type { ForgeQuery } from '../../../components/ForgeQueryBuilder';
import type { FieldConfig } from '../../../components/ForgeQueryBuilder/types';
import { mergeDefaultQueryWithRemembered } from '../../../components/ForgeQueryBuilder/query';

const normalizeText = (v: unknown) => String(v ?? '').trim();

export const formatRiskQualitative = (riskQualitative?: string) => {
  const qualitative = normalizeText(riskQualitative);
  const norm = qualitative.toLowerCase();

  const isHigh =
    qualitative === '不通过' ||
    norm.includes('high_risk') ||
    norm.includes('high-risk') ||
    norm.includes('fail') ||
    qualitative.includes('高风险') ||
    qualitative.includes('拒绝');
  const isPass = qualitative === '通过' || norm.includes('pass') || qualitative.includes('通过');
  const isPrePay =
    qualitative === '预支付' || qualitative === '预收' || norm.includes('pre_pay') || norm.includes('prepay') || qualitative.includes('预');

  const tagText = qualitative ? (isHigh ? '高风险' : qualitative) : '';
  const tagColor = isHigh ? 'red' : isPass ? 'blue' : isPrePay ? 'orange' : 'orange';
  const textColor = isHigh ? '#cf1322' : isPass ? '#1677ff' : isPrePay ? '#faad14' : '#faad14';

  return { qualitative, isHigh, isPass, isPrePay, tagText, tagColor, textColor };
};

export const formatOrderRiskType = (params: { blindType?: string | null; riskType?: string | null }) => {
  const isBlind = normalizeText(params.blindType) === 'BLIND';
  const rawRiskType = normalizeText(params.riskType);
  const baseLabel = !!params.blindType ? BlindTypeMap[params.blindType] : (RiskTypeMap as any)[rawRiskType] || rawRiskType || '-';

  const { tagText, tagColor } = formatRiskQualitative(baseLabel);
  const label = baseLabel === '-' ? '-' : tagText || baseLabel;
  const color = baseLabel === '-' ? 'default' : isBlind ? 'red' : tagColor;

  return { label, color };
};

export type OrderListInitialState = {
  forcedQuery?: ForgeQuery;
  forcedViewMode?: string;
  forcedTabKey?: string;
  initialSceneKey?: string;
  initialQuery: ForgeQuery;
};

import { getDefaultQuery } from '../order.effect';

/** 解析 URL 中的 q 参数为查询对象 */
export const parseForgeQueryFromSearch = (search: string): ForgeQuery | undefined => {
  const searchParams = new URLSearchParams(search);
  const value = searchParams.get('q');
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (e) {
    try {
      const parsed = JSON.parse(decodeURIComponent(value));
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (err) {
      return undefined;
    }
  }
  return undefined;
};

/** 解析路由与入参，生成订单列表的初始状态 */
export const buildOrderListInitialState = (params: {
  search: string;
  defaultScene?: string;
  queryFields: FieldConfig[];
  tabScene?: string;
}): OrderListInitialState => {
  const searchParams = new URLSearchParams(params.search);
  const forcedQuery = parseForgeQueryFromSearch(params.search);
  const forcedViewMode = searchParams.get('v') || undefined;
  const forcedTabKey = searchParams.get('t') || undefined;
  const initialSceneKey = forcedTabKey || params.defaultScene;
  const defaultQuery = getDefaultQuery(initialSceneKey);

  const { query } = mergeDefaultQueryWithRemembered(params.queryFields, forcedQuery ? undefined : defaultQuery);
  return {
    forcedQuery,
    forcedViewMode,
    forcedTabKey,
    initialSceneKey,
    initialQuery: forcedQuery || query
  };
};

export const generateOrderUrl = (params: {
  orderNo: string;
  targetScene?: string;
  targetPath?: string;
  model?: string;
}) => {
  const { orderNo, targetScene, targetPath = window.location.pathname, model = 'list' } = params;
  const queryObj = {
    filter: {
      order_no: { operator: 'eq', value: orderNo },
    },
  };
  // We construct a fresh URLSearchParams, preserving current ones might not be desired if we jump to another page
  // But if we jump to another page, we usually want clean state + our filter.
  // If we stay on same page, we might want to preserve other params? 
  // For safety, let's just use the necessary params for the target view.

  const search = new URLSearchParams();
  search.set('v', model);
  if (targetScene) {
    search.set('t', targetScene);
  }
  search.set('q', JSON.stringify(queryObj));

  return `${window.location.origin}${targetPath}?${search.toString()}`;
};
